import { WidgetContainer } from '@/components/analytics/WidgetContainer';
import type { DailyKnowledgeStreak as StreakData } from '@/types/analytics';
import styles from './DailyKnowledgeStreak.module.scss';

export interface DailyKnowledgeStreakProps {
  data: StreakData | null;
  isLoading?: boolean;
  error?: Error | null;
}

/**
 * Daily Knowledge Streak Widget
 *
 * Displays current and longest Daily Knowledge streaks with milestones.
 * Shows flame icon and motivational messaging based on streak length.
 */
export function DailyKnowledgeStreak({ data, isLoading, error }: DailyKnowledgeStreakProps) {
  const isEmpty = !data || data.currentStreak === 0;

  // Get milestone emoji and message
  const getMilestone = (streak: number) => {
    if (streak >= 100) return { emoji: '💯', message: 'Century Streak!', color: 'var(--primary)' };
    if (streak >= 30) return { emoji: '🏆', message: 'Month Streak!', color: 'var(--warning)' };
    if (streak >= 14) return { emoji: '⭐', message: 'Two Week Streak!', color: 'var(--success)' };
    if (streak >= 7) return { emoji: '🔥', message: 'Week Streak!', color: 'var(--error)' };
    if (streak >= 3) return { emoji: '🌱', message: 'Building Momentum!', color: 'var(--success)' };
    return { emoji: '📚', message: 'Getting Started!', color: 'var(--text-secondary)' };
  };

  const currentMilestone = data ? getMilestone(data.currentStreak) : null;
  const longestMilestone = data ? getMilestone(data.longestStreak) : null;

  return (
    <WidgetContainer
      title="Daily Knowledge Streak"
      description=""
      isLoading={isLoading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage="Complete a Daily Knowledge session to start your streak!"
      size="medium"
    >
      <div className={styles.streak}>
        {/* Current Streak */}
        <div className={styles.streak__current}>
          <div className={styles.streak__value}>{data?.currentStreak || 0}</div>
          <div className={styles.streak__label}>Current Streak</div>
          {currentMilestone && (
            <div
              className={styles.streak__milestone}
              style={{ color: currentMilestone.color }}
            >
              {currentMilestone.message}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className={styles.streak__divider} />

        {/* Stats Grid */}
        <div className={styles.streak__stats}>
          <div className={styles.streak__stat}>
            <span className={styles.streak__statValue}>
              {data?.longestStreak || 0}
            </span>
            <span className={styles.streak__statLabel}>Longest Streak</span>
            {longestMilestone && data && data.longestStreak > data.currentStreak && (
              <span className={styles.streak__statBadge}>
                {longestMilestone.emoji}
              </span>
            )}
          </div>

          <div className={styles.streak__stat}>
            <span className={styles.streak__statValue}>
              {data?.lastCompletedDate
                ? new Date(data.lastCompletedDate).toLocaleDateString()
                : 'Never'}
            </span>
            <span className={styles.streak__statLabel}>Last Completed</span>
          </div>
        </div>

        {/* Motivational Message */}
        {data && data.currentStreak > 0 && (
          <div className={styles.streak__motivation}>
            {data.currentStreak === data.longestStreak
              ? "🎯 You're at your personal best! Keep it going!"
              : `💪 ${data.longestStreak - data.currentStreak} days to beat your record!`}
          </div>
        )}
      </div>
    </WidgetContainer>
  );
}
